import { useEffect, useState } from "react";
import axios from "axios";
import { useTaal } from "@/contexts/TaalContext";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

/**
 * ZL5: de weekmeting als trend. Besluit Frank ZL5-1, 27 oktober 2026.
 *
 * Elke zondagnacht beantwoordt het lokale model de twaalf gouden vragen met vaste
 * bronnen (`backend/services/weekmeting.py`); per week staat hier hoeveel van de
 * feiten erin stonden, met de duimen en overnames van die week ernaast.
 *
 * **HET VERSCHIL MET DE VORIGE WEEK STAAT ER ALLEEN ALS ER TWEE GETALLEN ZIJN.**
 * Een overgeslagen week heeft geen getal, en dan is er niets om mee te vergelijken;
 * een verschil tegen niets zou een stijging verzinnen.
 *
 * **ZONDER ANTWOORD VAN DE SERVER STAAT ER NIETS**, net als bij de telling van de
 * voorstellen: een lege lijst die eigenlijk "onbekend" betekent, leest als "nog nooit
 * gemeten".
 */
export function verschil(weken, i) {
  const nu = weken[i];
  const vorige = weken.slice(i + 1).find((w) => w.totaal != null);
  if (nu?.totaal == null || !vorige) return null;
  return Math.round(nu.totaal * 100) - Math.round(vorige.totaal * 100);
}

export default function WeekMeting() {
  const { t, taal } = useTaal();
  const [data, setData] = useState(null);

  useEffect(() => {
    let levend = true;
    axios
      .get(`${API}/memory/weekmeting`)
      .then(({ data: d }) => { if (levend) setData(d); })
      .catch(() => {});
    return () => { levend = false; };
  }, []);

  if (!data) return null;
  const weken = data.weken || [];

  return (
    <div className="mt-3 border-t pt-3" data-testid="weekmeting-blok">
      <h3 className="text-sm font-medium">{t("Weekmeting")}</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        {t("Elke zondagnacht beantwoordt het lokale model twaalf vaste vragen uit vaste bronnen. Per week staat hier hoeveel van de feiten in het antwoord stonden, met jouw duimen en overnames van die week.")}
      </p>
      {weken.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="weekmeting-leeg">
          {t("Nog geen meting. De eerste komt zondagnacht tussen 2 en 6 uur, als de Mac aan staat.")}
        </p>
      ) : (
        <ul className="mt-2 space-y-1 text-xs">
          {weken.map((w, i) => {
            const d = verschil(weken, i);
            const datum = new Date(w.gemeten_op).toLocaleDateString(taal === "nl" ? "nl-NL" : "en-GB",
              { day: "numeric", month: "short", year: "numeric" });
            return (
              <li key={w.gemeten_op} data-testid="weekmeting-week" className="flex flex-wrap gap-x-3">
                <span className="w-24 shrink-0">{datum}</span>
                {w.totaal == null ? (
                  <span className="text-muted-foreground">{t("overgeslagen: {reden}", { reden: w.reden })}</span>
                ) : (
                  <span data-testid="weekmeting-totaal">
                    {Math.round(w.totaal * 100)}%
                    {d != null && d !== 0 && (
                      <span className={d < 0 ? "ml-1 text-destructive" : "ml-1"} data-testid="weekmeting-verschil">
                        ({d > 0 ? "+" : ""}{d})
                      </span>
                    )}
                  </span>
                )}
                <span className="text-muted-foreground">
                  {t("duim {op} omhoog, {neer} omlaag; {over} overgenomen, {aan} aangepast",
                    { op: w.duim_omhoog, neer: w.duim_omlaag, over: w.overgenomen, aan: w.aangepast })}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
